import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  defaultBotVoiceId,
  formatCoachRate,
  getBotVoiceRate,
  isCoachVoiceSupported,
  parseVoiceId,
  speakBotLine,
  stepBotVoiceRate,
  stopBotVoices,
  subscribeCoachRate,
} from "./CoachVoice";
import { buildBotAdd3Comment, buildBotFinalComment, buildBotRivalComment, createBotCommentPicker } from "../services/botRivalComments";
import "./botCommentary.css";

// «Comentar partida de los bots» (solo administradores): partidas «Solo bots» y partidas
// del administrador contra bots. Cada bot, justo después de jugar, comenta SU jugada en
// primera persona hablándole a su rival (bot o persona). Con 2 jugadores cada jugada se
// valora con el análisis del Entrenador (/trainer/bot-classify), que además dice qué pasó
// en el tablero (captura, dominación, especial) y si la victoria ya es segura.
// Los comentarios van en cola: nunca se corta al que habla; si se acumulan, se dice el último.
// Cada bot tiene su bocina y su «evaluación visual» (insignia de la valoración de su jugada).

const VOICE_KEY = "xima_bot_commentary_voice_on";
const MUTED_KEY = "xima_bot_commentary_muted_v1";
const EVAL_KEY = "xima_bot_commentary_eval_v1";
const SLOT_COLORS = ["#38bdf8", "#fb7185", "#fbbf24", "#34d399"];
const MAX_WAITING = 1; // comentarios listos esperando turno (el resto se descarta)
const FLAG_PRAISE_AT = [2, 7, 13]; // comentarios propios en los que halaga la bandera del rival
// Voz elegida para cada bot en «Jugar con bot» (admin): { slot: "auto" | "male1" | … }.
export const BOT_VOICE_CHOICE_KEY = "xima_bot_voice_choice_v1";
export const readBotVoiceChoices = () => {
  try {
    const value = JSON.parse(window.localStorage.getItem(BOT_VOICE_CHOICE_KEY) || "null");
    return value && typeof value === "object" ? value : {};
  } catch (error) {
    return {};
  }
};
export const writeBotVoiceChoices = (value) => {
  try {
    window.localStorage.setItem(BOT_VOICE_CHOICE_KEY, JSON.stringify(value || {}));
  } catch (error) {
    /* sin almacenamiento */
  }
};
const botVoiceIdFor = (slot, choices) => {
  const chosen = choices && choices[slot];
  return parseVoiceId(chosen) ? chosen : defaultBotVoiceId(slot);
};

export const BOT_EVAL_LABELS = {
  brillante: "Brillante",
  mejor: "La mejor",
  excelente: "Excelente",
  buena: "Buena",
  libro: "De libro",
  interesante: "Interesante",
  imprecision: "Imprecisión",
  error: "Error",
  grave: "Error grave",
};

const readJson = (key, fallback) => {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "null");
    return value && typeof value === "object" ? value : fallback;
  } catch (error) {
    return fallback;
  }
};
const writeJson = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    /* sin almacenamiento */
  }
};
const readVoiceOn = () => {
  try {
    return window.localStorage.getItem(VOICE_KEY) !== "0";
  } catch (error) {
    return true;
  }
};

// Estado compartido entre el bocadillo y los botones de cada panel.
const store = {
  muted: readJson(MUTED_KEY, {}), // slot -> true
  evalOn: readJson(EVAL_KEY, {}), // slot -> true
  badges: {}, // slot -> { kind, step }
  lines: {}, // slot -> { text, speaking } (lo último que dijo cada bot, en su columna)
  listeners: new Set(),
};
const notify = () => store.listeners.forEach((fn) => fn({}));
const useBotCommentaryStore = () => {
  const [, setTick] = useState({});
  useEffect(() => {
    store.listeners.add(setTick);
    return () => store.listeners.delete(setTick);
  }, []);
  return store;
};
const toggleMuted = (slot) => {
  store.muted = { ...store.muted, [slot]: !store.muted[slot] };
  writeJson(MUTED_KEY, store.muted);
  if (store.muted[slot]) stopBotVoices();
  notify();
};
const toggleEval = (slot) => {
  store.evalOn = { ...store.evalOn, [slot]: !store.evalOn[slot] };
  writeJson(EVAL_KEY, store.evalOn);
  notify();
};

// «Bot_Vector_749» → «Bot Vector» (así se lee y se ve mejor).
export const cleanBotName = (name) =>
  String(name || "")
    .replace(/_/g, " ")
    .replace(/\s*\d+$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();

const cleanMoves = (moves) =>
  (Array.isArray(moves) ? moves : [])
    .map((entry) => ({
      playerIndex: Math.max(0, Math.floor(Number(entry?.playerIndex) || 0)),
      notation: String(entry?.notation || "").trim().toLowerCase(),
    }))
    .filter((entry) => entry.notation);

const SpeakerIcon = ({ on }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
    {on ? (
      <>
        <path d="M16 8.5a5 5 0 0 1 0 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M18.5 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ) : (
      <path d="M16 9l5 6M21 9l-5 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    )}
  </svg>
);

// Bocinita + «evaluación visual» de un bot (va dentro del panel de ese bot, abajo a la derecha).
// Velocidad de la voz de los bots (−/1×/+): se aplica desde la siguiente frase.
function BotRateControl() {
  const [rate, setRate] = useState(getBotVoiceRate);
  useEffect(() => subscribeCoachRate(() => setRate(getBotVoiceRate())), []);
  return (
    <span className="bot-rate-control" title="Velocidad de la voz de los bots">
      <button type="button" className="bot-panel-btn" onClick={(event) => { event.stopPropagation(); stepBotVoiceRate(-1); }} aria-label="Más despacio">
        −
      </button>
      <b>{formatCoachRate(rate)}</b>
      <button type="button" className="bot-panel-btn" onClick={(event) => { event.stopPropagation(); stepBotVoiceRate(1); }} aria-label="Más rápido">
        +
      </button>
    </span>
  );
}

// Texto de lo que dijo el bot (en su columna, debajo de su panel).
export function BotLine({ slot }) {
  const state = useBotCommentaryStore();
  const line = state.lines[slot];
  if (!line || !line.text) return null;
  return (
    <div className={`bot-line${line.speaking ? " is-speaking" : ""}`} style={{ "--bc-color": SLOT_COLORS[slot % 4] }} role="status" aria-live="polite">
      {line.text}
    </div>
  );
}

export function BotPanelControls({ slot, compact = false, name = "", withLine = true }) {
  const state = useBotCommentaryStore();
  const muted = Boolean(state.muted[slot]);
  const evalOn = Boolean(state.evalOn[slot]);
  const badge = evalOn ? state.badges[slot] : null;
  const who = name ? ` de ${name}` : "";
  return (
    <span className={`bot-panel-controls${compact ? " is-compact" : ""}`} style={{ "--bc-color": SLOT_COLORS[slot % 4] }}>
      {badge ? (
        <span className={`bot-eval-badge is-${badge.kind}`} title="Valoración de su última jugada">
          <img src={`/replay-analysis/${badge.kind}.png?v=2`} alt="" />
          {BOT_EVAL_LABELS[badge.kind] || badge.kind}
        </span>
      ) : null}
      <button
        type="button"
        className={`bot-panel-btn bot-panel-eval${evalOn ? " is-on" : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          toggleEval(slot);
        }}
        title={evalOn ? `Ocultar la evaluación visual${who}` : `Ver la evaluación visual${who}`}
        aria-label={evalOn ? `Ocultar la evaluación visual${who}` : `Ver la evaluación visual${who}`}
        aria-pressed={evalOn}
      >
        ★
      </button>
      {isCoachVoiceSupported() ? (
        <button
          type="button"
          className={`bot-panel-btn bot-panel-voice${muted ? "" : " is-on"}`}
          onClick={(event) => {
            event.stopPropagation();
            toggleMuted(slot);
          }}
          title={muted ? `Activar la voz${who}` : `Callar${who}`}
          aria-label={muted ? `Activar la voz${who}` : `Callar${who}`}
          aria-pressed={!muted}
        >
          <SpeakerIcon on={!muted} />
        </button>
      ) : null}
      {isCoachVoiceSupported() ? <BotRateControl /> : null}
      {withLine ? <BotLine slot={slot} /> : null}
    </span>
  );
}

// players: [{ slot, name, isBot, country, female }] por puesto.
// panelsInline: los botones de cada bot van dentro de sus paneles (pantalla de ordenador);
// si no, se muestran aquí en una fila propia.
export default function BotCommentary({
  tournamentId,
  moves,
  players = [],
  playersCount = 2,
  inProgress,
  gameEnded,
  panelsInline = false,
  onMoveBadge,
}) {
  useBotCommentaryStore();
  const [voiceOn, setVoiceOn] = useState(() => isCoachVoiceSupported() && readVoiceOn());
  const voiceOnRef = useRef(voiceOn);
  voiceOnRef.current = voiceOn;
  const playersRef = useRef(players);
  playersRef.current = players;
  const badgeRef = useRef(onMoveBadge);
  badgeRef.current = onMoveBadge;

  const stateRef = useRef(null);
  if (!stateRef.current) {
    stateRef.current = {
      handled: 0, // jugadas ya atendidas
      lastBySlot: {}, // última jugada de cada uno: { kind, tag, notation }
      saidBySlot: {}, // comentarios dichos por cada bot
      queue: [],
      busy: false,
      finalSaid: false,
      pick: createBotCommentPicker(),
      alive: true,
      hideTimer: null,
      game: "",
      script: [], // guion: lo que dijo cada bot, tal cual
      scriptTimer: null,
      voiceChoices: readBotVoiceChoices(),
    };
  }

  const twoPlayers = Number(playersCount) === 2;
  const playerOf = (slot) => playersRef.current[slot] || {};
  const nameOf = (slot) => {
    const player = playerOf(slot);
    return player.isBot === false ? String(player.name || "").trim() || "Jugador" : cleanBotName(player.name) || `Bot ${slot + 1}`;
  };
  const isBotSlot = (slot) => playerOf(slot).isBot !== false;
  // Voz y género de cada bot: la elegida en «Jugar con bot» o puesto 0 hombre, 1 mujer, 2 hombre, 3 mujer.
  const voiceOf = (slot) => botVoiceIdFor(slot, stateRef.current.voiceChoices);
  const personOf = (slot) => {
    const player = playerOf(slot);
    const human = player.isBot === false;
    return {
      name: nameOf(slot),
      female: human ? false : voiceOf(slot).startsWith("female"),
      human,
      country: String(player.country || ""),
    };
  };

  // Guion: se guarda en el servidor mientras se juega (poco a poco y al final).
  const saveScript = (now = false) => {
    const state = stateRef.current;
    clearTimeout(state.scriptTimer);
    const send = () => {
      if (!state.script.length) return;
      axios.post("/trainer/bot-script", { tournamentId: state.game, entries: state.script }).catch(() => {});
    };
    if (now) send();
    else state.scriptTimer = setTimeout(send, 2500);
  };
  const recordScript = (item, extra = {}) => {
    const state = stateRef.current;
    state.script.push({
      step: Number(item.step) || 0,
      slot: item.slot,
      name: nameOf(item.slot),
      text: item.text,
      voice: voiceOf(item.slot),
      kind: extra.kind || "",
      final: extra.final === true,
    });
    saveScript(extra.final === true);
  };

  // Dice los comentarios de la cola uno tras otro.
  const pump = () => {
    const state = stateRef.current;
    if (state.busy || !state.alive) return;
    const item = state.queue.shift();
    if (!item) return;
    state.busy = true;
    clearTimeout(state.hideTimer);
    store.lines = { ...store.lines, [item.slot]: { text: item.text, speaking: true } };
    notify();
    const speak = voiceOnRef.current && !store.muted[item.slot];
    const spoken = speak ? speakBotLine(item.text, voiceOf(item.slot)) : new Promise((resolve) => setTimeout(resolve, 2600));
    spoken.then(() => {
      if (!state.alive) return;
      state.busy = false;
      if (store.lines[item.slot] && store.lines[item.slot].text === item.text) {
        store.lines = { ...store.lines, [item.slot]: { text: item.text, speaking: false } };
        notify();
      }
      pump();
    });
  };

  const enqueue = (item) => {
    const state = stateRef.current;
    state.queue.push(item);
    while (state.queue.length > MAX_WAITING) state.queue.shift();
    pump();
  };

  // Partida nueva: todo de cero.
  useEffect(() => {
    const state = stateRef.current;
    const key = String(tournamentId || "");
    if (state.game === key) return;
    state.game = key;
    state.handled = 0;
    state.lastBySlot = {};
    state.saidBySlot = {};
    state.queue = [];
    state.finalSaid = false;
    state.script = [];
    state.voiceChoices = readBotVoiceChoices();
    state.pick = createBotCommentPicker();
    store.badges = {};
    store.lines = {};
    notify();
  }, [tournamentId]);

  // Cada jugada nueva: valorarla (2 jugadores) y, si la hizo un bot, comentarla.
  const movesKey = cleanMoves(moves)
    .map((entry) => `${entry.playerIndex}${entry.notation}`)
    .join(",");
  useEffect(() => {
    const state = stateRef.current;
    const list = cleanMoves(moves);
    if (!inProgress && !gameEnded) return;
    if (list.length < state.handled) state.handled = list.length; // la lista se rehízo
    // Al entrar con la partida ya empezada no se comentan jugadas viejas.
    if (state.handled === 0 && list.length > 1) state.handled = list.length - 1;
    if (list.length === state.handled) {
      if (list.length === 0 && twoPlayers && inProgress) {
        axios.post("/trainer/bot-prepare", { tournamentId, moves: [], side: 0 }).catch(() => {});
      }
      return;
    }
    const step = list.length;
    const last = list[step - 1];
    const before = list.slice(0, -1);
    state.handled = step;
    const slot = last.playerIndex;
    const previous = before[before.length - 1];
    const rivalSlot = twoPlayers ? (slot === 0 ? 1 : 0) : previous && previous.playerIndex !== slot ? previous.playerIndex : -1;
    const gameKey = String(tournamentId || "");

    const finishMove = ({ kind = "", events = null, forcedWin = false, pAfter = null, played = null }) => {
      if (!state.alive || state.game !== gameKey) return;
      if (kind) {
        store.badges = { ...store.badges, [slot]: { kind, step } };
        notify();
        if (store.evalOn[slot] && typeof badgeRef.current === "function") badgeRef.current(slot, played?.to || "", kind, step);
      }
      if (!isBotSlot(slot)) {
        // Jugada de la persona: no se comenta, pero el bot le contestará.
        const tag = events?.specialCaptured ? "specialCaptured" : events?.captured ? "captured" : events?.dominated ? "dominated" : "";
        state.lastBySlot[slot] = { kind, tag, notation: last.notation };
        return;
      }
      const said = (state.saidBySlot[slot] || 0) + 1;
      state.saidBySlot[slot] = said;
      const rival = rivalSlot >= 0 ? personOf(rivalSlot) : { name: "", human: false };
      const { text, tag } = buildBotRivalComment({
        notation: last.notation,
        kind,
        events,
        forcedWin,
        pAfter,
        me: personOf(slot),
        rival,
        rivalLast: rivalSlot >= 0 ? state.lastBySlot[rivalSlot] || null : null,
        flagPraise: Boolean(rival.country) && FLAG_PRAISE_AT.includes(said),
        pick: state.pick,
      });
      if (rivalSlot >= 0) delete state.lastBySlot[rivalSlot];
      state.lastBySlot[slot] = { kind, tag, notation: last.notation };
      recordScript({ slot, text, step }, { kind });
      enqueue({ slot, text, step });
    };

    if (!twoPlayers) {
      finishMove({});
      return;
    }
    axios
      .post("/trainer/bot-classify", {
        tournamentId,
        moves: before.map((entry) => ({ playerIndex: entry.playerIndex === 1 ? 1 : 0, notation: entry.notation })),
        side: slot === 1 ? 1 : 0,
        played: last.notation,
      })
      .then((response) => {
        const data = response?.data || {};
        finishMove({
          kind: data.ok && data.kind ? data.kind : "",
          events: data.events || null,
          forcedWin: data.forcedWin === true,
          pAfter: data.pAfter !== null && Number.isFinite(Number(data.pAfter)) ? Number(data.pAfter) : null,
          played: data.played || null,
        });
      })
      .catch(() => finishMove({}))
      .finally(() => {
        // Si ya llegó otra jugada, esa prepara la suya.
        if (!state.alive || state.handled !== step || gameEnded) return;
        axios
          .post("/trainer/bot-prepare", {
            tournamentId,
            moves: list.map((entry) => ({ playerIndex: entry.playerIndex === 1 ? 1 : 0, notation: entry.notation })),
            side: slot === 1 ? 0 : 1,
          })
          .catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movesKey, inProgress, gameEnded, tournamentId, twoPlayers]);

  // «+3 BLOQUES» entre dos bots: Unity propone y cada bot vota en el servidor; aquí se lee ese voto
  // y lo comentan el que propone y el que acepta o rechaza (tienen prioridad sobre la cola).
  const allBotsTwo = twoPlayers && players.length === 2 && players.every((player) => player.isBot !== false);
  useEffect(() => {
    if (!allBotsTwo || !inProgress || gameEnded) return undefined;
    const state = stateRef.current;
    let since = -1;
    let stopped = false;
    const poll = () => {
      axios
        .get("/live-eval/add3-events", { params: { since: Math.max(0, since) } })
        .then((response) => {
          if (stopped || !state.alive) return;
          const data = response?.data || {};
          if (since < 0) {
            since = Number(data.now) || 0; // lo de antes de empezar a mirar no cuenta
            return;
          }
          (Array.isArray(data.events) ? data.events : []).forEach((event) => {
            since = Math.max(since, Number(event.at) || 0);
            const responder = Number(event.botIndex) === 1 ? 1 : 0;
            const proposer = responder === 0 ? 1 : 0;
            const step = state.handled;
            const proposeType = state.add3RejectedBy === proposer ? "proposeAgain" : "propose";
            const answerType = event.accept
              ? state.add3RejectedBy === responder
                ? "acceptAgain"
                : "accept"
              : state.add3RejectedProposer === responder
              ? "rejectAgain"
              : "reject";
            const proposal = { slot: proposer, step, text: buildBotAdd3Comment(proposeType, state.pick) };
            const answer = { slot: responder, step, text: buildBotAdd3Comment(answerType, state.pick) };
            if (!event.accept) {
              state.add3RejectedBy = responder;
              state.add3RejectedProposer = proposer;
            }
            recordScript(proposal);
            recordScript(answer);
            state.queue = [proposal, answer];
            pump();
          });
        })
        .catch(() => {});
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBotsTwo, inProgress, gameEnded, tournamentId]);

  // Fin de la partida: el que hizo la última jugada llegó a la cima; cada bot se despide.
  useEffect(() => {
    const state = stateRef.current;
    if (!gameEnded || state.finalSaid) return;
    const list = cleanMoves(moves);
    if (!list.length) return;
    state.finalSaid = true;
    const winner = list[list.length - 1].playerIndex;
    const timer = setTimeout(() => {
      state.queue = [];
      const slots = Array.from({ length: Math.max(2, Number(playersCount) || 2) }, (_, index) => index);
      const loser = twoPlayers ? (winner === 0 ? 1 : 0) : -1;
      // Primero el ganador y luego el que perdió (sin descartar ninguna de las dos).
      [winner, ...slots.filter((slot) => slot !== winner)].forEach((slot) => {
        if (!isBotSlot(slot)) return;
        if (!twoPlayers && slot !== winner) return;
        const other = slot === winner ? (loser >= 0 ? loser : -1) : winner;
        const finalItem = {
          slot,
          step: list.length,
          text: buildBotFinalComment({
            won: slot === winner,
            me: personOf(slot),
            rival: other >= 0 ? personOf(other) : { name: "" },
            pick: state.pick,
          }),
        };
        state.queue.push(finalItem);
        recordScript(finalItem, { final: true });
      });
      pump();
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameEnded]);

  useEffect(
    () => () => {
      const state = stateRef.current;
      state.alive = false;
      clearTimeout(state.hideTimer);
      if (state.scriptTimer) saveScript(true);
      stopBotVoices();
    },
    []
  );

  const toggleVoice = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const next = !voiceOnRef.current;
    setVoiceOn(next);
    try {
      window.localStorage.setItem(VOICE_KEY, next ? "1" : "0");
    } catch (error) {
      /* sin almacenamiento */
    }
    if (!next) stopBotVoices();
  };

  const botSlots = players.map((player, slot) => ({ ...player, slot })).filter((player) => player.isBot !== false);
  if (panelsInline) return null;
  return (
    <div className="bot-commentary">
      {botSlots.length ? (
        <div className="bot-commentary-bar">
          {botSlots.map((player) => (
            <span key={player.slot} className="bot-commentary-chip" style={{ "--bc-color": SLOT_COLORS[player.slot % 4] }}>
              <span className="bot-commentary-chip-head">
                <b>{nameOf(player.slot)}</b>
                <BotPanelControls slot={player.slot} compact name={nameOf(player.slot)} withLine={false} />
              </span>
              <BotLine slot={player.slot} />
            </span>
          ))}
          {isCoachVoiceSupported() ? (
            <button
              type="button"
              className={`bot-commentary-voice${voiceOn ? " is-on" : ""}`}
              onClick={toggleVoice}
              title={voiceOn ? "Silenciar a todos los bots" : "Activar la voz de los bots"}
              aria-label={voiceOn ? "Silenciar a todos los bots" : "Activar la voz de los bots"}
              aria-pressed={voiceOn}
            >
              <SpeakerIcon on={voiceOn} />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
